import { useState, useEffect, useCallback } from "react";
import React from "react";
import {
  TableHead,
  Table,
  TableBody,
  TableRow,
  Paper,
  TableContainer,
  TableCell,
  Popover,
  TableSortLabel,
  IconButton,
  TablePagination,
  Typography,
  MenuItem,
  Chip,
  Tooltip,
} from "@mui/material";
import { GetAllAdmins, DeleteAdmin, ResendInvitation } from "../../api/endpoints";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { successNote, toastErrorNotify } from "../../helpers/toastNotify";
// import { FormatListNumbered } from "@mui/icons-material";
import DeleteButtonWithConfirmation from "../../layout/DeleteButtonWithConfirmation";
import apiClient from "../../api/apiClient";
import CenteredLoading from "../../layout/CenteredLoading";

const AdminList = () => {
  const [adminData, setAdminData] = useState(null);
  const [adminId, setAdminId] = useState(null);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [resendingInvitation, setResendingInvitation] = useState(false);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleSortRequest = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const sortedData = adminData?.sort((a, b) => {
    const isAsc = order === "asc";
    if (orderBy === "name") {
      return isAsc
        ? a?.name?.localeCompare(b.name)
        : b?.name?.localeCompare(a.name);
    }
  });

  // ! Get Admin User List
  const getAdminUser = useCallback(async () => {
    try {
      const adminUsers = await apiClient.get(GetAllAdmins);
      setAdminData(adminUsers.data.data);
      setLoading(false);
    } catch (err) {
      console.error(err);
    }
  }, [adminData]);

  const paginatedData = adminData?.slice(
    page * rowsPerPage,
    (page + 1) * rowsPerPage
  );
  // console.log(adminData);

  // ! Delete Admin User

  async function deleteAdmin() {
    try {
      const params = {
        adminId,
      };
      await apiClient.post(DeleteAdmin, params);
      successNote("Admin-bruker slettet");
      handleCloseMenu();
      getAdminUser();
    } catch (err) {
      if (!err.response) {
        toastErrorNotify("Ingen svar fra serveren");
      } else if (err.response.data?.data) {
        toastErrorNotify(err.response.data.data?.validationErrors[0]);
      } else if (err.response.data.data === null) {
        toastErrorNotify(err.response.data?.message);
      } else {
        toastErrorNotify("Mislyktes");
      }
    }
  }

  useEffect(() => {
    getAdminUser();
  }, []);

  // ! Resend Invitation (2026-09-07, see backend/CLAUDE.md - previously an admin whose
  // invitation had expired had to be deleted and re-invited from scratch; this lets a
  // still-listed, not-yet-registered admin get a fresh invite instead).
  async function resendInvitation() {
    if (!selectedAdmin?.email) return;
    setResendingInvitation(true);
    try {
      await apiClient.post(ResendInvitation, { email: selectedAdmin.email });
      successNote("Ny invitasjon sendt");
      handleCloseMenu();
    } catch (err) {
      // Deliberately not passing err.response.data.message straight through anymore - it's the
      // raw backend string, in English, and doesn't tell the admin what to do about it (e.g.
      // "The new invitation could not be send because The OTP has not been found."). A Norwegian,
      // actionable message is shown instead; see backend/CLAUDE.md's ResendInvitation notes if the
      // backend gains distinct, documented error codes worth branching on here.
      if (!err.response) {
        toastErrorNotify("Ingen svar fra serveren");
      } else {
        toastErrorNotify(
          "Kunne ikke sende ny invitasjon. Hvis administratoren fortsatt ikke er registrert, prøv å slette brukeren og opprette en ny administrator."
        );
      }
    } finally {
      setResendingInvitation(false);
    }
  }

  // Invitations are valid 10 days (see backend/CLAUDE.md - ResendInvitation/CheckInvitationStatus).
  // GetAllAdmins doesn't return the OTP's own expiry, so "Utløpt" is inferred from how long ago
  // the admin row was created; the backend is the source of truth if that window ever changes.
  const INVITATION_VALID_DAYS = 10;

  // Opprettelsesdato is shown in Norwegian day/month/year order (e.g. "17/02/2026"),
  // not the browser/locale-dependent order toLocaleString() would otherwise produce.
  function formatNorwegianDate(dateInput) {
    const d = new Date(dateInput);
    if (Number.isNaN(d.getTime())) return "";
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  }

  function getRegistrationStatus(data) {
    const isRegistered = !(data.name === null && data.surname === null);
    if (isRegistered) {
      return { key: "registered", label: "Registrert", color: "success" };
    }
    const createdMs = new Date(data.creationDateTime).getTime();
    const ageMs = Date.now() - createdMs;
    const expired = !Number.isNaN(createdMs) && ageMs > INVITATION_VALID_DAYS * 24 * 60 * 60 * 1000;
    return expired
      ? { key: "expired", label: "Invitasjon utløpt", color: "error" }
      : { key: "pending", label: "Venter på registrering", color: "warning" };
  }

  function getCustomRoleName(roleName) {
    // roleName is the raw claim string from the backend (see
    // Core/Extensions/ClaimExtensions.Roles) - the real super-admin claim is
    // "org.admin.super", not the bare "org.admin" this used to special-case.
    // The switch's default used to silently return "Super-administrator" for
    // ANY unrecognized roleName (a typo, a future role type, null/undefined),
    // making an unmatched role indistinguishable from an actual Super Admin
    // in this list. Now unrecognized roles show visibly as unrecognized
    // instead of pretending to be the highest-privilege role.
    switch (roleName) {
      case "org.admin.super":
        return "Super-administrator";
      case "org.admin.department":
        return "Enhets-administrator";
      case "org.admin.course":
        return "Avdelings-administrator";
      case "org.admin.integration":
        return "Integrasjons-administrator";
      case "org.admin.assistant":
        return "System-administrator";
      default:
        return roleName ? `Ukjent rolle (${roleName})` : "Ukjent rolle";
    }
  }
  
  
  

  // ! Handle 3 Dots Menu in the List

  const handleOpenMenu = (event) => {
    setOpen(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setOpen(false);
  };

  return (
    <>
      {loading ? (
        <CenteredLoading tekst={"Laster inn…"} />
      ) : adminData?.length < 1 ? (
        <Typography sx={{ color: "warning.main" }}>
          Det finnes ingen administratorbrukere ennå
        </Typography>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ maxWidth: 1000 }}>
            <Table
              sx={{ minWidth: 650 }}
              aria-label="simple table"
              size="small"
            >
              <TableHead className="table-header">
                <TableRow>
                  <TableCell sx={{ color: "#ffffff" }}>Roller</TableCell>
                  <TableCell sx={{ color: "#ffffff", paddingRight: 2 }}>
                    <TableSortLabel
                      active={orderBy === "name"}
                      direction={orderBy === "name" ? order : "asc"}
                      onClick={(event) => handleSortRequest(event, "name")}
                      sx={{
                        "&.MuiButtonBase-root.MuiTableSortLabel-root": {
                          color: "#fff",
                        },
                        "&.MuiButtonBase-root.MuiTableSortLabel-root:hover": {
                          color: "#fff",
                        },
                        "& .MuiTableSortLabel-icon": {
                          color: "#fff !important",
                        },
                      }}
                    >
                      Navn
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ color: "#ffffff", paddingRight: 2 }}>
                    E-postadresse
                  </TableCell>
                  <TableCell sx={{ color: "#ffffff", paddingRight: 2 }}>
                    Mobilnummer
                  </TableCell>
                  <TableCell sx={{ color: "#ffffff" }}>
                    Opprettelsesdato
                  </TableCell>
                  <TableCell sx={{ color: "#ffffff" }}>
                    Registreringsstatus
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/*// ! Mapping the admin users list */}

                {paginatedData?.map((data, index) => (
                  <TableRow
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                      "&:nth-of-type(even)": {
                        backgroundColor: "#f4f5f5",
                      },
                    }}
                    key={index}
                  >
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {data?.roles &&
                        getCustomRoleName(data?.roles[1]?.roleName)}
                    </TableCell>
                    <TableCell>
                      {data.name === null && data.surname === null
                        ? "Denne admin-brukeren har ikke blitt registrert"
                        : `${data.name} ${data.surname}`}
                    </TableCell>
                    <TableCell>{data.email}</TableCell>
                    <TableCell>{data.phone}</TableCell>
                    <TableCell>
                      {formatNorwegianDate(data.creationDateTime)}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const status = getRegistrationStatus(data);
                        const tooltip =
                          status.key === "expired"
                            ? "Invitasjonslenken er over 10 dager gammel og virker ikke lenger – send en ny invitasjon."
                            : status.key === "pending"
                            ? "Administratoren har fått en invitasjon, men har ikke fullført registreringen ennå."
                            : "Administratoren har fullført registreringen.";
                        return (
                          <Tooltip title={tooltip}>
                            <Chip size="small" color={status.color} label={status.label} variant="outlined" />
                          </Tooltip>
                        );
                      })()}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="large"
                        color="inherit"
                        onClick={(e) => {
                          handleOpenMenu(e);
                          setAdminId(data.adminId);
                          setSelectedAdmin(data);
                        }}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[10, 15, 25]}
              component="div"
              count={adminData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              labelRowsPerPage="Rader pr. side"
            />
          </TableContainer>

          {/* // ! Popover Functions */}
          <Popover
            open={Boolean(open)}
            anchorEl={open}
            onClose={handleCloseMenu}
            anchorOrigin={{ vertical: "top", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              sx: {
                p: 1,
                width: 220,
                "& .MuiMenuItem-root": {
                  px: 1,
                  typography: "body2",
                  borderRadius: 0.75,
                  whiteSpace: "nowrap",
                },
              },
            }}
          >
            {/* <MenuItem>
        <Iconify icon={'eva:edit-fill'} sx={{ mr: 2 }} />
        Update
      </MenuItem> */}
            {selectedAdmin &&
              getRegistrationStatus(selectedAdmin).key !== "registered" && (
                <MenuItem onClick={resendInvitation} disabled={resendingInvitation}>
                  <MailOutlineIcon sx={{ mr: 2 }} />
                  Send ny invitasjon
                </MenuItem>
              )}
            <DeleteButtonWithConfirmation
              onDeleteConfirm={deleteAdmin}
              color="#f44336"
              icon={<DeleteOutlineIcon />}
              buttonText={<>Slett</>}
            />
          </Popover>
        </>
      )}
    </>
  );
};

export default AdminList;
